import { useLayoutEffect, useRef, useState } from "react";

const MAX_VISIBLE_LINES = 6;

let measureContext = null;
function getMeasureContext() {
  if (!measureContext && typeof document !== "undefined") {
    measureContext = document.createElement("canvas").getContext("2d");
  }
  return measureContext;
}

/**
 * Picks the first hint (longest first) that fits on one line in the box, so the
 * placeholder never wraps or gets cut off. `hints` must be a stable array.
 * Re-checks when the box is resized and once web fonts have loaded.
 */
function useFittingHint(inputRef, hints) {
  const [hint, setHint] = useState(hints[hints.length - 1]);

  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;
    let cancelled = false;

    const fit = () => {
      const ctx = getMeasureContext();
      const style = window.getComputedStyle(el);
      const width =
        el.clientWidth - (parseFloat(style.paddingInlineStart) || 0) - (parseFloat(style.paddingInlineEnd) || 0);
      if (cancelled || !ctx || !(width > 0)) return;
      ctx.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const fitting = hints.find((candidate) => ctx.measureText(candidate).width <= width - 4);
      setHint(fitting || hints[hints.length - 1]);
    };

    fit();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", fit);
      return () => {
        cancelled = true;
        window.removeEventListener("resize", fit);
      };
    }
    // Deferred a frame: reacting inside the observer callback re-lays out the
    // field and trips "ResizeObserver loop completed with undelivered notifications".
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    observer.observe(el);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [inputRef, hints]);

  return hint;
}

/**
 * The search box's text field: looks like a one-line input at rest, grows with
 * the text up to MAX_VISIBLE_LINES lines, then scrolls. Enter submits (via
 * `onSubmit`), Shift+Enter inserts a line break, and Enter during IME
 * composition is left to the IME.
 *
 * The look comes from `className` (the search box passes gd-search__input),
 * defaulting to the gd-textarea and app-request-input classes; the growth
 * limit is computed from the element's own line height, padding and border.
 */
export default function RequestTextInput({
  value,
  onChange,
  onSubmit,
  hints,
  className = "",
  ...rest
}) {
  const ref = useRef(null);
  const hint = useFittingHint(ref, hints);

  // Re-measured on every value change and whenever the box's width changes
  // (a different width wraps the same text onto a different number of lines).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const resize = () => {
      const style = window.getComputedStyle(el);
      const lineHeight = parseFloat(style.lineHeight) || parseFloat(style.fontSize) * 1.5;
      const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
      const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
      const maxHeight = lineHeight * MAX_VISIBLE_LINES + padding + border;
      el.style.height = "auto";
      const wanted = el.scrollHeight + border; // scrollHeight leaves the border out
      el.style.height = `${Math.min(wanted, maxHeight)}px`;
      el.style.overflowY = wanted > maxHeight ? "auto" : "hidden";
    };
    resize();
    if (typeof ResizeObserver === "undefined") return undefined;
    let lastWidth = el.clientWidth;
    let frame = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (el.clientWidth === lastWidth) return;
        lastWidth = el.clientWidth;
        resize();
      });
    });
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    e.preventDefault();
    onSubmit();
  };

  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={hint}
      className={className || "gd-textarea app-request-input"}
      {...rest}
    />
  );
}

/**
 * Button label that never changes the button's width: every label is laid out
 * in the same grid cell (the widest sets the width) and only the active one is
 * visible.
 */
export function StableLabel({ labels, active }) {
  return (
    <span className="app-stable-label">
      {labels.map((label) => (
        <span
          key={label}
          className={`app-stable-label__item${label === active ? "" : " app-stable-label__item--hidden"}`}
        >
          {label}
        </span>
      ))}
    </span>
  );
}
